import React, { useEffect, useMemo, useState } from 'react';
import { relatorioRespostasApi, RespostaPorData } from '../api/api';

function fmt(n: number) {
  return n.toLocaleString('pt-BR');
}

function fmtData(d: string) {
  if (!d) return '—';
  // Garante que a string esteja no formato YYYY-MM-DD antes de parsear
  const ymd = d.substring(0, 10);
  const dt = new Date(ymd + 'T00:00:00');
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div
      className="rounded-2xl border p-5 bg-white border-gray-100"
    >
      <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">{label}</p>
      <p className="text-2xl font-black text-gray-900" style={{ letterSpacing: '-0.02em' }}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function getUsuarioNome(value: string | null | undefined): string {
  const normalized = (value ?? '').trim();
  return normalized || 'Sem usuário';
}

export default function RelatorioRespostasPage() {
  const [dados, setDados] = useState<RespostaPorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [usuarioFiltro, setUsuarioFiltro] = useState('');

  useEffect(() => {
    relatorioRespostasApi
      .getPorData()
      .then(r => setDados(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const usuarios = useMemo(() => {
    const set = new Set<string>();
    dados.forEach(d => set.add(getUsuarioNome(d.usuarioNome as string | null | undefined)));
    return Array.from(set).sort();
  }, [dados]);

  const filtrado = useMemo(() => {
    return dados.filter(d => {
      const ymd = (d.data ?? '').substring(0, 10);
      if (dataInicio && ymd < dataInicio) return false;
      if (dataFim && ymd > dataFim) return false;
      if (usuarioFiltro && getUsuarioNome(d.usuarioNome as string | null | undefined) !== usuarioFiltro) return false;
      return true;
    });
  }, [dados, dataInicio, dataFim, usuarioFiltro]);

  const totalDisparos  = filtrado.reduce((s, d) => s + d.totalDisparos, 0);
  const totalRespostas = filtrado.reduce((s, d) => s + d.totalRespostas, 0);
  const totalDias      = new Set(filtrado.map(d => d.data)).size;
  const totalUsuarios  = new Set(filtrado.map(d => getUsuarioNome(d.usuarioNome as string | null | undefined))).size;

  // Agrupar linhas por data para mesclar a célula de data visualmente
  const linhasPorData = useMemo(() => {
    const map = new Map<string, RespostaPorData[]>();
    filtrado.forEach(row => {
      const key = (row.data ?? '').substring(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    });
    return Array.from(map.entries()).sort(([a], [b]) => (a > b ? -1 : 1));
  }, [filtrado]);

  return (
    <div className="space-y-5 max-w-7xl">
      {/* Título */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Respostas por Usuário</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Disparos realizados e respostas recebidas por usuário, agrupados por data
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Disparos realizados" value={fmt(totalDisparos)} />
        <StatCard label="Respostas recebidas" value={fmt(totalRespostas)} />
        <StatCard label="Dias" value={fmt(totalDias)} />
        <StatCard label="Usuários" value={fmt(totalUsuarios)} />
      </div>

      {/* Tabela */}
      <div
        className="rounded-2xl border overflow-hidden bg-white border-gray-100"
      >
        {/* Filtros */}
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
          {usuarios.length > 1 && (
            <select
              value={usuarioFiltro}
              onChange={e => setUsuarioFiltro(e.target.value)}
              className="h-9 pl-3 pr-8 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
            >
              <option value="">Todos os usuários</option>
              {usuarios.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 whitespace-nowrap">De</span>
            <input
              type="date"
              value={dataInicio}
              onChange={e => setDataInicio(e.target.value)}
              className="h-9 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 whitespace-nowrap">Até</span>
            <input
              type="date"
              value={dataFim}
              onChange={e => setDataFim(e.target.value)}
              className="h-9 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
            />
          </div>

          {(dataInicio || dataFim || usuarioFiltro) && (
            <button
              onClick={() => { setDataInicio(''); setDataFim(''); setUsuarioFiltro(''); }}
              className="h-9 px-3 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Limpar filtros
            </button>
          )}

          <span className="text-xs text-gray-400 ml-auto">
            {filtrado.length} registro{filtrado.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-sm text-gray-400">
            Carregando...
          </div>
        ) : filtrado.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">Nenhum dado encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400 text-left w-36">
                    Data
                  </th>
                  <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400 text-left">
                    Usuário
                  </th>
                  <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400 text-right">
                    Disparos
                  </th>
                  <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400 text-right">
                    Respostas
                  </th>
                </tr>
              </thead>

              <tbody>
                {linhasPorData.map(([data, linhas]) => {
                  const totDia = linhas.reduce((s, l) => s + l.totalRespostas, 0);
                  const totDis = linhas.reduce((s, l) => s + l.totalDisparos, 0);
                  return (
                    <React.Fragment key={data}>
                      {linhas.map((row, idx) => (
                        <tr
                          key={`${data}-${getUsuarioNome(row.usuarioNome as string | null | undefined)}`}
                          className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          {/* Célula de data: apenas na primeira linha do grupo */}
                              {idx === 0 ? (
                            <td
                              className="px-5 py-3 text-sm font-semibold text-gray-700 align-top"
                              rowSpan={linhas.length}
                            >
                              {fmtData(data)}
                            </td>
                          ) : null}

                          {/* Usuário */}
                          {(() => {
                            const usuarioNome = getUsuarioNome(row.usuarioNome as string | null | undefined);
                            return (
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-gray-900"
                                style={{ background: stringToColor(usuarioNome) }}
                              >
                                {usuarioNome.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm text-gray-700">{usuarioNome}</span>
                            </div>
                          </td>
                            );
                          })()}

                          {/* Disparos */}
                          <td className="px-5 py-3 text-right">
                            <span className="text-sm font-medium text-gray-700">{fmt(row.totalDisparos)}</span>
                          </td>

                          {/* Respostas */}
                          <td className="px-5 py-3 text-right">
                            {row.totalRespostas > 0 ? (
                              <span
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700"
                              >
                                {fmt(row.totalRespostas)}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}

                      {/* Subtotal do dia (só se houver mais de um usuário no dia) */}
                      {linhas.length > 1 && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td className="px-5 py-2" />
                          <td className="px-5 py-2">
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                              Subtotal do dia
                            </span>
                          </td>
                          <td className="px-5 py-2 text-right">
                            <span className="text-sm font-bold text-gray-600">{fmt(totDis)}</span>
                          </td>
                          <td className="px-5 py-2 text-right">
                            <span className="text-sm font-bold text-gray-600">{fmt(totDia)}</span>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>

              {/* Total geral */}
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td className="px-5 py-3" colSpan={2}>
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Total geral</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-sm font-bold text-gray-900">{fmt(totalDisparos)}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-sm font-bold text-gray-900">{fmt(totalRespostas)}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Gera uma cor consistente a partir do nome do usuário
function stringToColor(str: string | null | undefined): string {
  const colors = [
    '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B',
    '#EF4444', '#06B6D4', '#EC4899', '#6366F1',
  ];
  const safe = getUsuarioNome(str);
  let hash = 0;
  for (let i = 0; i < safe.length; i++) hash = safe.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
